import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, MapPin, Phone, Mail, CheckCircle, Star, Users, ArrowRight } from 'lucide-react';
import FounderSection from '../components/FounderSection';
import usePageTitle from '../hooks/usePageTitle';

const AboutPage = () => {
  usePageTitle('About Us');
  const values = [
    { title: 'Accuracy First', desc: 'Every return undergoes multiple reviews before filing. We never rush — we get it right.' },
    { title: 'Confidentiality', desc: 'Your financial information is encrypted and never shared without your explicit consent.' },
    { title: 'Accessibility', desc: 'Premium tax expertise shouldn\'t be out of reach. We keep our pricing transparent and fair.' },
    { title: 'Year-Round Partner', desc: 'Tax planning is a 12-month conversation, not a once-a-year event.' },
  ];

  return (
    <div style={{ paddingTop: '72px' }}>
      {/* Hero */}
      <section className="py-20 px-6" style={{ background: '#0B1F3A' }}>
        <div className="max-w-7xl mx-auto text-center">
          <div className="label-uppercase mb-4" style={{ color: '#D4AF37' }}>About Us</div>
          <h1 className="font-heading text-5xl font-extrabold text-white tracking-tight mb-6">
            Meet MaTaxPreparer LLC
          </h1>
          <p className="text-blue-200 text-lg max-w-3xl mx-auto">
            Founded in 2021 by Fajlur Rahman, PTP, CPE — we're a nationwide tax firm built on accuracy, trust, and personalized service. No templates, no cookie-cutter advice. Just real expertise for real people.
          </p>
        </div>
      </section>

      {/* Founder Section - Full Version */}
      <FounderSection variant="full" />

      {/* Values */}
      <section className="py-20 px-6 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="label-uppercase text-[#2563EB] mb-3">Our Values</div>
            <h2 className="font-heading text-4xl font-bold text-[#0B1F3A] tracking-tight">
              What We Stand For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-2xl p-7 border border-gray-100 hover:shadow-floating transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: '#0B1F3A' }}>
                  <Shield size={24} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-heading font-bold text-[#0B1F3A] text-lg mb-3">{v.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-bold text-[#0B1F3A] tracking-tight mb-10">Find Us</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: MapPin, label: 'Address', value: '4384 Laughlin Ct NW\nKennesaw, GA 30144', href: null },
              { icon: Phone, label: 'Phone', value: '770-235-6999', href: 'tel:7702356999' },
              { icon: Mail, label: 'Email', value: 'mataxpreparerllc@yahoo.com', href: 'mailto:mataxpreparerllc@yahoo.com' },
            ].map(({ icon: Icon, label, value, href }) => (
              <div key={label} className="p-7 rounded-2xl border border-gray-100 hover:shadow-card transition-all duration-200">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: '#0B1F3A10' }}>
                  <Icon size={24} style={{ color: '#0B1F3A' }} />
                </div>
                <div className="font-semibold text-[#0B1F3A] mb-2">{label}</div>
                {href ? (
                  <a href={href} className="text-[#2563EB] hover:underline text-sm whitespace-pre-line">{value}</a>
                ) : (
                  <div className="text-[#475569] text-sm whitespace-pre-line">{value}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6" style={{ background: '#0B1F3A' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl font-bold text-white tracking-tight mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-blue-200 text-lg mb-10">
            Book your free consultation today and discover how we can maximize your return.
          </p>
          <Link to="/book-appointment">
            <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-[#0B1F3A] hover:shadow-xl transition-all duration-300" style={{ background: '#D4AF37' }}>
              Book Free Consultation <ArrowRight size={18} />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
